import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, MessageCircleQuestion, Reply, Send, Trash2 } from 'lucide-react';
import { ModaleConfirmation } from '../contrats/ModaleConfirmation';
import { Chargement, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { momentRelatif } from '../../lib/moment-relatif';
import {
  adresseLecon,
  useDiscussionLecon,
  useGesteMessage,
  usePoserQuestion,
  useRepondreQuestion,
  type GesteMessage,
  type MessageDiscussion,
  type QuestionDiscussion,
  type VueLecon,
} from '../../lib/useLecteur';

/** Mêmes limites que l'API (`domain/discussion-module.ts`). */
export const TITRE_QUESTION_MAX = 150;
export const TEXTE_MESSAGE_MAX = 4000;

const CLASSE_CHAMP =
  'w-full rounded-xl border border-gray-200 bg-white p-3 text-sm text-codialis-navy focus:outline-none focus:ring-2 focus:ring-codialis-mint';

const CLASSE_BOUTON =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[44px]';

const CLASSE_GESTE =
  'inline-flex items-center gap-1 rounded text-xs text-codialis-gray transition hover:text-codialis-navy disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';

function dateComplete(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { timeZone: 'Indian/Reunion', dateStyle: 'long', timeStyle: 'short' });
}

function ResteCaracteres({ texte, max }: { texte: string; max: number }): JSX.Element | null {
  if (texte.length < max - 200) return null;
  return (
    <span className="text-[11px] text-codialis-gray">
      {texte.length} / {max} caractères
    </span>
  );
}

const LIBELLE_SUPPRESSION: Record<'question' | 'reponse', string> = {
  question: 'Supprimer la question',
  reponse: 'Supprimer la réponse',
};

const ICONES: Record<GesteMessage, typeof Eye> = { MASQUER: EyeOff, REAFFICHER: Eye, SUPPRIMER: Trash2 };

/**
 * Les gestes de qui lit le message, tels que l'API les propose : masquer ou
 * réafficher (modérateurs), supprimer — après confirmation. Un geste refusé
 * dit pourquoi au lieu d'appeler l'API.
 */
function GestesMessage({
  leconId,
  message,
  nature,
  reponses,
  children,
}: {
  leconId: string;
  message: MessageDiscussion;
  nature: 'question' | 'reponse';
  /** Réponses qui partiraient avec la question. */
  reponses: number;
  children?: ReactNode;
}): JSX.Element {
  const toast = useToast();
  const geste = useGesteMessage(leconId);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState(false);

  const faire = async (nom: GesteMessage) => {
    setErreur(null);
    try {
      await geste.mutateAsync({ messageId: message.id, geste: nom });
      toast(nom === 'MASQUER' ? 'Message masqué' : nom === 'REAFFICHER' ? 'Message réaffiché' : 'Message supprimé');
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Le geste n’a pas pu être fait.');
    } finally {
      setConfirmation(false);
    }
  };

  return (
    <>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 sm:pl-12">
        {children}
        {message.gestes.map((propose) => {
          const Icone = ICONES[propose.geste];
          return (
            <button
              key={propose.geste}
              type="button"
              disabled={geste.isPending}
              onClick={() => {
                if (propose.refus) setErreur(propose.refus);
                else if (propose.geste === 'SUPPRIMER') setConfirmation(true);
                else void faire(propose.geste);
              }}
              className={CLASSE_GESTE}
            >
              <Icone className="h-3.5 w-3.5" aria-hidden />
              {propose.libelle}
            </button>
          );
        })}
      </div>
      {erreur && (
        <div className="mt-2 sm:pl-12">
          <MessageErreur message={erreur} />
        </div>
      )}
      <ModaleConfirmation
        ouverte={confirmation}
        titre={LIBELLE_SUPPRESSION[nature]}
        message={
          nature === 'question' && reponses > 0
            ? `La question et ${reponses > 1 ? `ses ${reponses} réponses` : 'sa réponse'} seront supprimées définitivement.`
            : `${nature === 'question' ? 'La question' : 'La réponse'} sera supprimée définitivement.`
        }
        libelleConfirmer="Supprimer"
        enCours={geste.isPending}
        onConfirmer={() => void faire('SUPPRIMER')}
        onAnnuler={() => setConfirmation(false)}
      />
    </>
  );
}

/** Avatar, nom, badges et moment d'un message, comme la maquette. */
function EnteteMessage({
  message,
  question,
  children,
}: {
  message: MessageDiscussion;
  question: boolean;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <div
        aria-hidden
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-sm font-bold text-white"
      >
        {message.auteur.initiales}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-semibold text-codialis-navy">{message.auteur.nom}</span>
          {message.auteur.badge && (
            <span className="badge-success rounded px-1.5 py-0.5 text-[10px] font-bold uppercase">{message.auteur.badge}</span>
          )}
          {question && <span className="badge-info rounded px-1.5 py-0.5 text-[10px] font-bold">QUESTION</span>}
          <time dateTime={message.creeLe} title={dateComplete(message.creeLe)} className="text-xs text-codialis-gray">
            • {momentRelatif(message.creeLe)}
          </time>
        </div>
        {children}
        {message.masque && (
          <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-amber-800">
            <EyeOff className="h-3.5 w-3.5" aria-hidden />
            {message.masque.mention}
          </p>
        )}
      </div>
    </div>
  );
}

function TexteMessage({ texte }: { texte: string }): JSX.Element {
  return <p className="mt-2 whitespace-pre-line break-words text-sm text-codialis-gray sm:pl-12">{texte}</p>;
}

function CarteReponse({ leconId, reponse }: { leconId: string; reponse: MessageDiscussion }): JSX.Element {
  return (
    <li
      className={`rounded-xl border-l-2 p-4 ${
        reponse.masque
          ? 'border-amber-300 bg-amber-50/60'
          : reponse.miseEnAvant
            ? 'border-codialis-mint bg-codialis-mint/10'
            : 'border-gray-200 bg-gray-50'
      }`}
    >
      <EnteteMessage message={reponse} question={false} />
      <TexteMessage texte={reponse.texte} />
      {reponse.gestes.length > 0 && <GestesMessage leconId={leconId} message={reponse} nature="reponse" reponses={0} />}
    </li>
  );
}

function CarteQuestion({
  vue,
  question,
  ciblee,
}: {
  vue: VueLecon;
  question: QuestionDiscussion;
  ciblee: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const repondre = useRepondreQuestion(vue.lecon.id);
  /** Brouillon de la réponse ; `null` tant que la zone n'est pas ouverte. */
  const [reponse, setReponse] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const leconId = vue.lecon.id;

  const publier = async () => {
    if (reponse === null || !reponse.trim()) return;
    setErreur(null);
    try {
      await repondre.mutateAsync({ questionId: question.id, texte: reponse });
      setReponse(null);
      toast('Réponse publiée');
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La réponse n’a pas pu être publiée.');
    }
  };

  const clavier = (evenement: KeyboardEvent<HTMLTextAreaElement>) => {
    if (evenement.key === 'Enter' && (evenement.ctrlKey || evenement.metaKey)) {
      evenement.preventDefault();
      void publier();
    } else if (evenement.key === 'Escape') {
      setReponse(null);
    }
  };

  const lecon = question.lecon;
  return (
    <li id={`question-${question.id}`} className="scroll-mt-24 space-y-3">
      <article
        aria-label={`Question : ${question.titre}`}
        className={`rounded-xl p-4 ${question.masque ? 'border border-dashed border-amber-300 bg-amber-50/60' : 'bg-gray-50'} ${
          ciblee ? 'ring-2 ring-codialis-mint' : ''
        }`}
      >
        <EnteteMessage message={question} question>
          <h4 className="mt-1 break-words text-sm font-medium text-codialis-navy">{question.titre}</h4>
          {lecon && (
            <p className="mt-0.5 text-xs text-codialis-gray">
              {lecon.id === leconId ? (
                <>Posée depuis cette leçon</>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(adresseLecon(lecon.id))}
                  className="text-left font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                >
                  Posée depuis la leçon {lecon.numero} · {lecon.titre}
                </button>
              )}
            </p>
          )}
        </EnteteMessage>
        <TexteMessage texte={question.texte} />
        <GestesMessage leconId={leconId} message={question} nature="question" reponses={question.reponses.length}>
          {question.refusReponse === null && reponse === null && (
            <button type="button" onClick={() => setReponse('')} className={CLASSE_GESTE}>
              <Reply className="h-3.5 w-3.5" aria-hidden />
              Répondre
            </button>
          )}
        </GestesMessage>
      </article>

      {(question.reponses.length > 0 || reponse !== null) && (
        <ol aria-label={`Réponses à « ${question.titre} »`} className="ml-4 space-y-3 sm:ml-8">
          {question.reponses.map((une) => (
            <CarteReponse key={une.id} leconId={leconId} reponse={une} />
          ))}
          {reponse !== null && (
            <li className="space-y-2 rounded-xl border-l-2 border-gray-200 bg-white p-3">
              <textarea
                value={reponse}
                rows={3}
                maxLength={TEXTE_MESSAGE_MAX}
                autoFocus
                onChange={(evenement) => setReponse(evenement.target.value)}
                onKeyDown={clavier}
                aria-label={`Votre réponse à « ${question.titre} »`}
                placeholder="Votre réponse…"
                className={`${CLASSE_CHAMP} resize-y`}
              />
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="min-w-0 flex-1">
                  <ResteCaracteres texte={reponse} max={TEXTE_MESSAGE_MAX} />
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setReponse(null);
                    setErreur(null);
                  }}
                  className={`${CLASSE_BOUTON} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => void publier()}
                  disabled={!reponse.trim() || repondre.isPending}
                  className={`${CLASSE_BOUTON} bg-codialis-navy text-white hover:bg-codialis-deep`}
                >
                  {repondre.isPending ? 'Publication…' : 'Répondre'}
                </button>
              </div>
              {erreur && <MessageErreur message={erreur} />}
            </li>
          )}
        </ol>
      )}
    </li>
  );
}

/** Zone « Poser une question », sous le fil comme dans la maquette. */
function NouvelleQuestion({ leconId }: { leconId: string }): JSX.Element {
  const toast = useToast();
  const poser = usePoserQuestion(leconId);
  const [titre, setTitre] = useState('');
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const publier = async () => {
    if (!titre.trim() || !texte.trim()) return;
    setErreur(null);
    try {
      await poser.mutateAsync({ titre, texte });
      setTitre('');
      setTexte('');
      toast('Question publiée');
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La question n’a pas pu être publiée.');
    }
  };

  const clavier = (evenement: KeyboardEvent<HTMLTextAreaElement>) => {
    if (evenement.key === 'Enter' && (evenement.ctrlKey || evenement.metaKey)) {
      evenement.preventDefault();
      void publier();
    }
  };

  return (
    <form
      onSubmit={(evenement) => {
        evenement.preventDefault();
        void publier();
      }}
      aria-label="Poser une question"
      className="mt-6 space-y-2 border-t border-gray-100 pt-4"
    >
      <input
        type="text"
        value={titre}
        maxLength={TITRE_QUESTION_MAX}
        onChange={(evenement) => setTitre(evenement.target.value)}
        aria-label="Titre de la question"
        placeholder="Titre de votre question"
        className={CLASSE_CHAMP}
      />
      <textarea
        value={texte}
        rows={3}
        maxLength={TEXTE_MESSAGE_MAX}
        onChange={(evenement) => setTexte(evenement.target.value)}
        onKeyDown={clavier}
        aria-label="Votre question"
        placeholder="Poser une question au formateur ou aux autres apprenants…"
        className={`${CLASSE_CHAMP} resize-y`}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 flex-1 text-xs text-codialis-gray">
          <ResteCaracteres texte={texte} max={TEXTE_MESSAGE_MAX} />
          {texte.length < TEXTE_MESSAGE_MAX - 200 && 'Le formateur est prévenu de chaque nouvelle question.'}
        </span>
        <button
          type="submit"
          disabled={!titre.trim() || !texte.trim() || poser.isPending}
          className={`${CLASSE_BOUTON} bg-codialis-navy text-white hover:bg-codialis-deep`}
        >
          <Send className="h-4 w-4" aria-hidden />
          {poser.isPending ? 'Publication…' : 'Publier'}
        </button>
      </div>
      {erreur && <MessageErreur message={erreur} />}
    </form>
  );
}

/**
 * Onglet « Discussion » du lecteur (TF-448), sur le modèle de la maquette : le
 * fil du module de la leçon, commun à tous les apprenants qui lisent le cours.
 * Les questions les plus récentes d'abord, leurs réponses en retrait ; celles
 * du formateur et de la direction sont mises en évidence. L'auteur du cours et
 * la direction, en aperçu, répondent et modèrent ; l'API dit ce que chacun peut
 * faire de chaque message. `questionCiblee` (`?question=`, depuis une
 * notification) fait défiler jusqu'à la question.
 */
export function DiscussionModule({ vue, questionCiblee }: { vue: VueLecon; questionCiblee: string | null }): JSX.Element {
  const { data, isLoading, error } = useDiscussionLecon(vue.lecon.id, true);
  const defilee = useRef(false);

  useEffect(() => {
    if (!data || !questionCiblee || defilee.current) return;
    defilee.current = true;
    document.getElementById(`question-${questionCiblee}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [data, questionCiblee]);

  if (isLoading) return <Chargement libelle="Chargement de la discussion…" />;
  if (error || !data) {
    return <MessageErreur message={error instanceof ErreurApi ? error.message : 'La discussion n’a pas pu être chargée.'} />;
  }

  const cibleAbsente = questionCiblee !== null && !data.questions.some((question) => question.id === questionCiblee);

  return (
    <div>
      <h3 className="font-display text-xl font-bold text-codialis-navy">Discussion du module</h3>
      <p className="mb-4 text-sm text-codialis-gray">
        Module {data.module.numero} · {data.module.titre}
      </p>
      {data.moderation && data.refusQuestion && (
        <p className="mb-4 rounded-xl bg-codialis-sand/60 p-3 text-sm text-codialis-gray">
          {data.refusQuestion} Vous modérez cette discussion : masquez ou supprimez un message depuis ses gestes.
        </p>
      )}
      {cibleAbsente && (
        <p className="mb-4 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          Cette question n’est plus dans la discussion : elle a été retirée ou masquée.
        </p>
      )}

      {data.questions.length > 0 ? (
        <ol aria-label="Questions du module" className="space-y-4">
          {data.questions.map((question) => (
            <CarteQuestion key={question.id} vue={vue} question={question} ciblee={question.id === questionCiblee} />
          ))}
        </ol>
      ) : (
        <div className="flex flex-col items-center rounded-2xl bg-gray-50 px-4 py-8 text-center">
          <MessageCircleQuestion className="mb-2 h-8 w-8 text-codialis-gray/60" aria-hidden />
          <p className="text-sm text-codialis-gray">
            Aucune question dans ce module pour le moment.
            {data.refusQuestion === null && ' Posez la première : le formateur et les autres apprenants vous répondront ici.'}
          </p>
        </div>
      )}

      {data.refusQuestion === null ? (
        <NouvelleQuestion leconId={vue.lecon.id} />
      ) : (
        !data.moderation && <p className="mt-6 border-t border-gray-100 pt-4 text-sm text-codialis-gray">{data.refusQuestion}</p>
      )}
    </div>
  );
}
